import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  HStack,
  Heading,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import BulkActionBar from "../components/BulkActionBar";
import BulkEditModal from "../components/BulkEditModal";
import BulkSendEmailModal from "../components/BulkSendEmailModal";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import AddCreatorModal from "../components/AddCreatorModal";
import UploadCreatorsModal from "../components/UploadCreatorsModal";
import ExportCreatorsModal from "../components/ExportCreatorsModal";
import BooleanSearchHelp from "../components/BooleanSearchHelp";
import {
  CREATOR_STATUSES,
  CreatorsApi,
  type CreatorListResponse,
  type CreatorSortField,
  type SortDir,
} from "../api/creators";
import SortHeader from "../components/SortHeader";
import Pagination from "../components/Pagination";
import StatsPanel from "../components/StatsPanel";
import logger from "../analytics/logger";
import { useBasicSearchFlag } from "../analytics/featureFlags";
import type { CreatorStats } from "../api/creators";
import { fmtDate } from "../utils/datetime";
import { readSort, writeSort, SORT_KEYS } from "../lib/tableSort";

type VerifiedFilter = "" | "with" | "without";

type Form = {
  status: string[];
  origin: string;
  verified: VerifiedFilter;
  created_after: string;
  created_before: string;
  expr: string;
};

const EMPTY_FORM: Form = {
  status: [],
  origin: "",
  verified: "",
  created_after: "",
  created_before: "",
  expr: "",
};

function parseStatusList(v: string | null): string[] {
  return (v ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function formFromParams(p: URLSearchParams): Form {
  const verifiedRaw = (p.get("verified") ?? "").toLowerCase();
  const verified: VerifiedFilter =
    verifiedRaw === "with" || verifiedRaw === "without" ? verifiedRaw : "";
  return {
    status: parseStatusList(p.get("status")),
    origin: p.get("origin") ?? "",
    verified,
    created_after: p.get("created_after") ?? "",
    created_before: p.get("created_before") ?? "",
    // Back-compat: surface a bookmarked `?q=…` in the unified expression
    // input. The page never emits `?q=` itself after this change.
    expr: p.get("expr") ?? p.get("q") ?? "",
  };
}

const PREFS_KEY = "support_v2_creators_table_v1";
const COL_MIN = 60;
const COL_MAX = 600;
const DEFAULT_WIDTHS: Record<CreatorSortField, number> = {
  created_at: 140,
  name: 200,
  email: 260,
  status: 160,
  origin: 160,
  work_count: 90,
  verified: 90,
};

type TablePrefs = {
  widths: Partial<Record<CreatorSortField, number>>;
  sort?: CreatorSortField;
  dir?: SortDir;
};

function loadPrefs(): TablePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { widths: {} };
    const parsed = JSON.parse(raw);
    return {
      widths: parsed.widths ?? {},
      sort: parsed.sort,
      dir: parsed.dir,
    };
  } catch {
    return { widths: {} };
  }
}

function savePrefs(p: TablePrefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    // quota or disabled — non-fatal
  }
}

const MODE_KEY = "support_v2_creators_search_mode_v1";
type SearchMode = "basic" | "advanced";

function loadMode(): SearchMode {
  try {
    return localStorage.getItem(MODE_KEY) === "advanced" ? "advanced" : "basic";
  } catch {
    return "basic";
  }
}

function saveMode(m: SearchMode) {
  try {
    localStorage.setItem(MODE_KEY, m);
  } catch {
    // quota or disabled — non-fatal
  }
}

function paramsHaveAdvanced(p: URLSearchParams): boolean {
  return (
    !!p.get("status") ||
    !!p.get("origin") ||
    !!p.get("verified") ||
    !!p.get("created_after") ||
    !!p.get("created_before") ||
    !!p.get("expr")
  );
}

export default function Creators() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get("page") ?? "1", 10) || 1);
  const pageSize = Math.max(1, parseInt(params.get("page_size") ?? "40", 10) || 40);

  const [draft, setDraft] = useState<Form>(() => formFromParams(params));
  const [mode, setMode] = useState<SearchMode>(() =>
    paramsHaveAdvanced(params) ? "advanced" : loadMode(),
  );
  useEffect(() => {
    saveMode(mode);
  }, [mode]);
  const [origins, setOrigins] = useState<string[]>([]);
  const [data, setData] = useState<CreatorListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchTick, setRefetchTick] = useState(0);
  const basicSearchEnabled = useBasicSearchFlag();
  const [stats, setStats] = useState<CreatorStats | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const confirm = useDisclosure();
  const uploadDisclosure = useDisclosure();
  const exportDisclosure = useDisclosure();
  const addDisclosure = useDisclosure();
  const editDisclosure = useDisclosure();
  const sendEmailDisclosure = useDisclosure();
  const toast = useToast();

  // Fetched unconditionally: `by_status` also drives which statuses the filter
  // menu offers, and the advanced panel is reachable even under basic search.
  // The StatsPanel itself stays flag-gated below.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const s = await CreatorsApi.stats();
        if (active) setStats(s);
      } catch (e) {
        logger.error("creators_stats_failed", e);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    CreatorsApi.origins().then(setOrigins).catch((e) => logger.error("creators_origins_failed", e));
  }, []);

  // refetchTick is bumped after bulk-delete to force a re-fetch without
  // mutating URL params. Selection is cleared on each successful load — it is
  // only meaningful for the rows currently visible.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const d = await CreatorsApi.list({
          page,
          page_size: pageSize,
          status: params.get("status") ?? undefined,
          origin: params.get("origin") ?? undefined,
          verified:
            params.get("verified") === "with"
              ? "with"
              : params.get("verified") === "without"
                ? "without"
                : undefined,
          created_after: params.get("created_after") ?? undefined,
          created_before: params.get("created_before") ?? undefined,
          expr: params.get("expr") ?? undefined,
          sort: (params.get("sort") as CreatorSortField | null) ?? undefined,
          dir: (params.get("dir") as SortDir | null) ?? undefined,
        });
        if (!active) return;
        setData(d);
        setSelectedIds(new Set());
      } catch (e) {
        if (!active) return;
        logger.error("creators_list_failed", e);
        const data =
          (e as { response?: { data?: { error?: string; at?: number; message?: string } } })
            ?.response?.data ?? {};
        if (data.error === "expr_parse_error") {
          const at = typeof data.at === "number" ? ` (at position ${data.at})` : "";
          setError(`Expression error: ${data.message || "invalid expression"}${at}`);
        } else {
          setError(data.error ?? "Failed to load creators");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [params, refetchTick, page, pageSize]);

  const refresh = () => {
    const next = new URLSearchParams();
    if (draft.status.length) next.set("status", draft.status.join(","));
    if (draft.origin) next.set("origin", draft.origin);
    if (draft.verified) next.set("verified", draft.verified);
    if (draft.created_after) next.set("created_after", draft.created_after);
    if (draft.created_before) next.set("created_before", draft.created_before);
    if (draft.expr.trim()) next.set("expr", draft.expr.trim());
    next.set("page", "1");
    setParams(next);
  };

  const reset = () => {
    setDraft(EMPTY_FORM);
    setParams(new URLSearchParams({ page: "1" }));
  };

  const toggleMode = () => {
    if (mode === "advanced") {
      // Switching to basic clears the advanced-only filter chips but keeps
      // the unified search expression — the input itself is identical in
      // both modes.
      setDraft({
        ...draft,
        status: [],
        origin: "",
        verified: "",
        created_after: "",
        created_before: "",
      });
      const next = new URLSearchParams();
      if (draft.expr) next.set("expr", draft.expr);
      const sortParam = params.get("sort");
      const dirParam = params.get("dir");
      if (sortParam) next.set("sort", sortParam);
      if (dirParam) next.set("dir", dirParam);
      next.set("page", "1");
      setParams(next);
      setMode("basic");
    } else {
      setMode("advanced");
    }
  };

  const setPageSize = (size: number) => {
    const next = new URLSearchParams(params);
    next.set("page_size", String(size));
    next.set("page", "1");
    setParams(next);
  };

  const setPage = (p: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next);
  };

  const [prefs, setPrefs] = useState<TablePrefs>(() => loadPrefs());

  // On mount only: if URL has no sort/dir, seed it from the remembered
  // sessionStorage sort (defaults to created_at desc if nothing remembered).
  useEffect(() => {
    if (!params.get("sort")) {
      const remembered = readSort(SORT_KEYS.creators);
      const next = new URLSearchParams(params);
      next.set("sort", remembered.sort);
      next.set("dir", remembered.dir);
      setParams(next, { replace: true });
    }
    // Intentionally mount-only: re-seeding on every params change would fight
    // the user's own sort clicks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const remembered = readSort(SORT_KEYS.creators);
  const sort = (params.get("sort") as CreatorSortField | null) ?? (remembered.sort as CreatorSortField);
  const dir = (params.get("dir") as SortDir | null) ?? remembered.dir;

  // Persist the active sort to sessionStorage (the source of truth read back on
  // next mount via readSort). Column widths stay in the localStorage prefs blob;
  // we no longer mirror sort/dir into React state here (nothing reads them, and
  // set-state-in-effect flags it).
  useEffect(() => {
    writeSort(SORT_KEYS.creators, { sort, dir });
  }, [sort, dir]);

  const widthOf = (f: CreatorSortField) =>
    prefs.widths[f] ?? DEFAULT_WIDTHS[f];

  const setWidth = useCallback((f: CreatorSortField, w: number) => {
    setPrefs((p) => {
      const next: TablePrefs = {
        ...p,
        widths: { ...p.widths, [f]: Math.max(COL_MIN, Math.min(COL_MAX, Math.round(w))) },
      };
      savePrefs(next);
      return next;
    });
  }, []);

  const visibleIds = data?.creators.map((c) => c.id) ?? [];
  const selectedOnPage = visibleIds.filter((id) => selectedIds.has(id)).length;
  const allOnPageSelected = visibleIds.length > 0 && selectedOnPage === visibleIds.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  const toggleAllOnPage = (checked: boolean) => {
    setSelectedIds(() => (checked ? new Set(visibleIds) : new Set()));
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const doBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setDeleting(true);
    try {
      const res = await CreatorsApi.bulkDelete(ids);
      toast({
        title: `Deleted ${res.deleted.length} creator${res.deleted.length === 1 ? "" : "s"}`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setSelectedIds(new Set());
      setRefetchTick((t) => t + 1);
      confirm.onClose();
    } catch (e) {
      logger.error("creators_bulk_delete_failed", e);
      toast({
        title: "Delete failed",
        description:
          (e as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const toggleSort = (field: CreatorSortField) => {
    const next = new URLSearchParams(params);
    if (sort === field) {
      next.set("dir", dir === "asc" ? "desc" : "asc");
    } else {
      next.set("sort", field);
      // Created_at and work_count default to descending — newest/most first.
      next.set("dir", field === "created_at" || field === "work_count" ? "desc" : "asc");
    }
    next.set("page", "1");
    setParams(next);
  };

  // Only offer statuses at least one creator actually holds. Counts come from
  // /creators/stats (whole table, independent of the current filters). Falls
  // back to the full list until stats land, and always keeps an already-checked
  // status visible so a bookmarked `?status=` stays togglable.
  const statusOptions = CREATOR_STATUSES.filter(
    (s) => !stats || (stats.by_status[s] ?? 0) > 0 || draft.status.includes(s),
  );

  const sortHeaderProps = (field: CreatorSortField) => ({
    active: sort === field,
    dir,
    onToggle: () => toggleSort(field),
    width: widthOf(field),
    onResize: (w: number) => setWidth(field, w),
    onResetWidth: () => setWidth(field, DEFAULT_WIDTHS[field]),
  });

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">Creators</Heading>
        <HStack spacing={2}>
          <Button colorScheme="blue" onClick={addDisclosure.onOpen}>
            Add creator
          </Button>
          <Button colorScheme="blue" onClick={uploadDisclosure.onOpen}>
            Bulk import
          </Button>
          <Button colorScheme="blue" onClick={exportDisclosure.onOpen}>
            Export
          </Button>
        </HStack>
      </HStack>

      {!basicSearchEnabled && stats && (
        <StatsPanel
          sections={[
            {
              title: "By status",
              entries: Object.entries(stats.by_status).map(([k, v]) => ({ label: k, value: v })),
            },
            {
              title: "Recently added",
              entries: [
                { label: "Past 24h", value: stats.added.day },
                { label: "Past 7d", value: stats.added.week },
                { label: "Past 30d", value: stats.added.month },
              ],
            },
          ]}
        />
      )}

      <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
        {(!basicSearchEnabled || mode === "advanced") && (
          <HStack spacing={3} wrap="wrap" mb={3}>
            <Menu closeOnSelect={false}>
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                fontWeight="normal"
                rightIcon={<ChevronDownIcon />}
                maxW="260px"
                textAlign="left"
              >
                {draft.status.length === 0
                  ? "All statuses"
                  : draft.status.length === 1
                    ? draft.status[0]
                    : `${draft.status.length} statuses`}
              </MenuButton>
              <MenuList maxH="320px" overflowY="auto" minW="240px">
                <MenuItem
                  onClick={() => setDraft({ ...draft, status: [] })}
                  fontStyle="italic"
                  color="gray.600"
                >
                  Clear all
                </MenuItem>
                <Stack spacing={1} px={3} py={2}>
                  {statusOptions.map((s) => (
                    <Checkbox
                      key={s}
                      size="sm"
                      isChecked={draft.status.includes(s)}
                      onChange={(e) =>
                        setDraft({
                          ...draft,
                          status: e.target.checked
                            ? [...draft.status, s]
                            : draft.status.filter((x) => x !== s),
                        })
                      }
                    >
                      {s}
                    </Checkbox>
                  ))}
                </Stack>
              </MenuList>
            </Menu>
            <Select
              size="sm"
              placeholder="All origins"
              value={draft.origin}
              onChange={(e) => setDraft({ ...draft, origin: e.target.value })}
              maxW="220px"
            >
              {origins.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </Select>
            <Select
              size="sm"
              value={draft.verified}
              onChange={(e) =>
                setDraft({ ...draft, verified: e.target.value as VerifiedFilter })
              }
              maxW="200px"
            >
              <option value="">All creators</option>
              <option value="with">Verified</option>
              <option value="without">Not verified</option>
            </Select>
            <HStack spacing={1}>
              <Input
                size="sm"
                type="date"
                value={draft.created_after}
                onChange={(e) => setDraft({ ...draft, created_after: e.target.value })}
                maxW="160px"
                title="Created on or after"
                aria-label="Created on or after"
              />
              <Box as="span" color="gray.500" fontSize="sm">
                –
              </Box>
              <Input
                size="sm"
                type="date"
                value={draft.created_before}
                onChange={(e) => setDraft({ ...draft, created_before: e.target.value })}
                maxW="160px"
                title="Created on or before"
                aria-label="Created on or before"
              />
            </HStack>
          </HStack>
        )}
        <HStack spacing={3} wrap="wrap">
          <Input
            size="sm"
            flex="1"
            minW="320px"
            placeholder={`Search… (or use field expressions like email = "*foo")`}
            value={draft.expr}
            onChange={(e) => setDraft({ ...draft, expr: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === "Enter") refresh();
            }}
          />
          <BooleanSearchHelp />
          <Button size="sm" colorScheme="blue" onClick={refresh}>
            Refresh
          </Button>
          <Button size="sm" variant="ghost" onClick={reset}>
            Reset
          </Button>
        </HStack>
        <HStack mt={2} minH="24px" justify="space-between" align="center">
          <Box>
            {basicSearchEnabled && (
              <Button variant="link" size="sm" onClick={toggleMode}>
                {mode === "basic" ? "Advanced search" : "Basic search"}
              </Button>
            )}
          </Box>
          <BulkActionBar
            count={selectedIds.size}
            onEdit={editDisclosure.onOpen}
            onDelete={confirm.onOpen}
            onSendEmail={sendEmailDisclosure.onOpen}
            entity="creator"
          />
        </HStack>
      </Box>

      <ConfirmDeleteDialog
        isOpen={confirm.isOpen}
        onClose={confirm.onClose}
        onConfirm={doBulkDelete}
        count={selectedIds.size}
        entity="creator"
        busy={deleting}
      />
      <AddCreatorModal
        isOpen={addDisclosure.isOpen}
        onClose={addDisclosure.onClose}
        onCreated={() => setRefetchTick((t) => t + 1)}
      />
      <BulkEditModal
        isOpen={editDisclosure.isOpen}
        onClose={editDisclosure.onClose}
        ids={[...selectedIds]}
        onSaved={() => {
          setSelectedIds(new Set());
          setRefetchTick((t) => t + 1);
        }}
      />
      <BulkSendEmailModal
        isOpen={sendEmailDisclosure.isOpen}
        onClose={sendEmailDisclosure.onClose}
        ids={[...selectedIds]}
        onSent={() => setSelectedIds(new Set())}
      />
      <UploadCreatorsModal
        isOpen={uploadDisclosure.isOpen}
        onClose={uploadDisclosure.onClose}
        onImported={refresh}
      />
      <ExportCreatorsModal
        isOpen={exportDisclosure.isOpen}
        onClose={exportDisclosure.onClose}
        params={params}
      />

      {error && (
        <Alert status="error" mb={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      {loading ? (
        <Box py={10} textAlign="center">
          <Spinner />
        </Box>
      ) : data ? (
        <>
          <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{ tableLayout: "fixed", width: "max-content" }}
          >
            <colgroup>
              <col style={{ width: "36px" }} />
              <col style={{ width: `${widthOf("created_at")}px` }} />
              <col style={{ width: `${widthOf("name")}px` }} />
              <col style={{ width: `${widthOf("email")}px` }} />
              <col style={{ width: `${widthOf("status")}px` }} />
              <col style={{ width: `${widthOf("origin")}px` }} />
              <col style={{ width: `${widthOf("work_count")}px` }} />
              <col style={{ width: `${widthOf("verified")}px` }} />
            </colgroup>
            <Thead>
              <Tr>
                <Th width="36px" px={2}>
                  <Checkbox
                    isChecked={allOnPageSelected}
                    isIndeterminate={someOnPageSelected}
                    onChange={(e) => toggleAllOnPage(e.target.checked)}
                    aria-label="Select all on this page"
                  />
                </Th>
                <SortHeader label="Created" {...sortHeaderProps("created_at")} />
                <SortHeader label="Name" {...sortHeaderProps("name")} />
                <SortHeader label="Email" {...sortHeaderProps("email")} />
                <SortHeader label="Status" {...sortHeaderProps("status")} />
                <SortHeader label="Origin" {...sortHeaderProps("origin")} />
                <SortHeader label="Works" isNumeric {...sortHeaderProps("work_count")} />
                <SortHeader label="Verified" {...sortHeaderProps("verified")} />
              </Tr>
            </Thead>
            <Tbody>
              {data.creators.map((c) => (
                <Tr
                  key={c.id}
                  cursor="pointer"
                  _hover={{ bg: "gray.50" }}
                  onClick={() => navigate(`/creators/${c.id}`)}
                >
                  <Td px={2} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      isChecked={selectedIds.has(c.id)}
                      onChange={(e) => toggleOne(c.id, e.target.checked)}
                      aria-label={`Select creator ${c.name}`}
                    />
                  </Td>
                  <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {fmtDate(c.created_at)}
                  </Td>
                  <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {c.name}
                  </Td>
                  <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {c.email}
                  </Td>
                  <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {c.status}
                  </Td>
                  <Td whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    {c.origin ?? "—"}
                  </Td>
                  <Td isNumeric>{c.work_count}</Td>
                  <Td
                    textAlign="center"
                    color="green.500"
                    fontWeight="bold"
                    aria-label={c.verified ? "Verified" : "Not verified"}
                  >
                    {c.verified ? "\u2713" : ""}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          </Box>
          <Pagination
            page={data.page}
            pageSize={data.page_size}
            total={data.total}
            onChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </>
      ) : null}
    </Box>
  );
}
